import type { RealtimeConnection } from 'modules/core/types';
import { ChannelSession } from './ChannelSession';
import { isInboundChannelMessage, type OutboundMessage, type ConnectionError, type InboundMessage } from './protocol';

import { setConnectionId } from './connectionRef';
import { ConnectionState } from './types';

/** Whoever the socket authenticates as — the impersonated user while impersonating. */
export type Identity = string | number;

export interface RealtimeServiceOptions {
  // `signal` aborts when the acting identity changes or the service stops, so a request belonging
  // to an identity we have left can be cancelled instead of running to completion.
  loadConnection: (signal: AbortSignal) => Promise<RealtimeConnection | null>;
  // `signal` aborts when the channel is released or the connection drops.
  loadChannelToken: (channel: string, signal: AbortSignal) => Promise<string>;
  heartbeatTimeout?: number; // ms; force reconnect if no frame arrives within this window
  tokenSkew?: number; // ms; treat the connection credential as expired this long before its expiry
}

// One ladder for the complete attempt. Loading a credential and opening a socket are different
// failure modes but not different lifecycles: whichever half failed, the next attempt starts from
// the top, so one counter orders them both. Reset by a successful handshake.
const RETRY_DELAYS = [10, 200, 500, 1000, 2000, 5000, 30000];
const RETRY_DELAY_MAX = 60000;

// Sessions live for a long time here, so the registry is swept on an interval rather than at the
// moment a lease is released.
const CHANNEL_SWEEP_INTERVAL = 600000;

/**
 * The whole realtime connection lifecycle for one acting identity: the credential, the WebSocket,
 * retries, timers, connection state and the channel registry.
 *
 * Recovery is driven by desired state rather than by queued commands. A connection attempt decides
 * for itself whether it first needs a credential, and once the socket is authenticated every
 * channel session reconciles what it still wants against what the server knows.
 */
export class RealtimeService {
  private ws: WebSocket | null = null;
  private intentionalClose = false;

  // Logical running state, owned by `start`/`stop`. Cleared the moment `stop()` is called so that
  // the deferred physical close never leaves a window in which the identity we are leaving can
  // reconnect.
  private active = false;
  private identity: Identity | undefined;

  private credential: RealtimeConnection | undefined;
  // Wall-clock ms when `credential` expires.
  private credentialExpiresAt: number | undefined;
  private readonly tokenSkew: number;
  // A 4001 close means the server rejected the credential. The first one asks for a fresh
  // credential; a second one — now on a credential we just minted — halts. Reset by a handshake.
  private credentialRejections = 0;

  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryCount = 0;
  // The generation whose credential request is in flight, or null. Held as the controller rather
  // than a boolean so a superseded attempt neither blocks the generation that replaced it nor
  // clears its flag when the abandoned request finally settles.
  private attemptGeneration: AbortController | null = null;

  // Identifies the current identity generation. Aborted when the identity changes or the service
  // stops, so an in-flight credential request knows it has been superseded.
  private generation = new AbortController();

  // Force a reconnect if the connection makes no progress within this window: half-open socket
  // detection, and the only bound on a socket handshake that never completes.
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly heartbeatTimeout: number;

  // Reconnecting while the page is hidden or offline produces doomed connections: a
  // sleeping machine authenticates during the sleep transition / dark wakes, then never
  // answers a ping and gets reaped by the server heartbeat. Skip those attempts and
  // remember we owe one; handleWake performs it when the page is visible and online.
  private reconnectOnWake = false;

  // StrictMode hack
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  private _state: ConnectionState = { status: 'idle' };
  private channels = new Map<string, ChannelSession>();
  private sweepTimer: ReturnType<typeof setInterval> | null = null;

  private refCounter: number = 0;
  private nextRef(): number {
    this.refCounter += 1;
    return this.refCounter;
  }

  constructor(public options: RealtimeServiceOptions) {
    this.heartbeatTimeout = options.heartbeatTimeout ?? 60000;
    this.tokenSkew = options.tokenSkew ?? 5000;
  }

  /** Activate for the acting identity. Paired with `stop` by the provider effect. */
  start(identity: Identity): void {
    const resuming = this.closeTimer !== null;
    if (resuming) {
      clearTimeout(this.closeTimer!);
      this.closeTimer = null;
    }

    // A different acting user: nothing from the identity we are leaving may be reused. Comparing
    // credentials used to do this, but loading one is ours now, so the provider's key is what tells
    // a Strict Mode remount apart from an identity swap.
    if (identity !== this.identity) {
      this.teardown();
      this.identity = identity;
    }

    this.active = true;
    this.intentionalClose = false;
    // `stop()` aborted it; a channel authorizing after the resume must not derive a dead signal.
    if (this.generation.signal.aborted) this.generation = new AbortController();
    this.armWakeListeners();
    if (this.sweepTimer === null) {
      this.sweepTimer = setInterval(() => this.sweepChannels(), CHANNEL_SWEEP_INTERVAL);
    }
    this.beginAttempt();
  }

  /** Drop the connection for the identity we're leaving so the socket can't outlive it. */
  stop(): void {
    // Prevent queueing multiple teardowns
    if (this.closeTimer !== null) return;

    // Logical shutdown is immediate: an owed wake reconnect, an armed retry and an in-flight
    // credential request all belong to the identity we are leaving and must not reach a socket.
    this.active = false;
    this.intentionalClose = true;
    this.disarmWakeListeners();
    this.clearRetryTimer();
    this.generation.abort();

    // Only the physical close is delayed, to outlive React's Strict Mode unmount/remount.
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.teardown();
    });
  }

  /** The socket, the credential and the channel subscriptions all go. */
  private teardown(): void {
    this.intentionalClose = true;
    this.clearHeartbeat();
    this.disarmWakeListeners();
    this.clearRetryTimer();
    this.retryCount = 0;
    this.reconnectOnWake = false;

    if (this.sweepTimer !== null) {
      clearInterval(this.sweepTimer);
      this.sweepTimer = null;
    }

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    // Idle before the channels reconcile, or a session still holding a lease plans a subscribe
    // against the socket we have just dropped.
    this.state = { status: 'idle' };
    this.resetChannels();
    this.invalidateCredential();
    this.credentialRejections = 0;
    this.newGeneration();
  }

  private handleWake = (): void => {
    if (!this.reconnectOnWake || document.hidden || !navigator.onLine) return;
    this.reconnectOnWake = false;
    this.retryCount = 0;
    this.beginAttempt();
  };

  private get isCredentialStale(): boolean {
    return this.credentialExpiresAt !== undefined && Date.now() >= this.credentialExpiresAt - this.tokenSkew;
  }

  private invalidateCredential(): void {
    this.credential = undefined;
    this.credentialExpiresAt = undefined;
  }

  private newGeneration(): void {
    this.generation.abort();
    this.generation = new AbortController();
  }

  /** @internal Frames can go out and a subscription would survive to be worth making. */
  get isConnected(): boolean {
    return this._state.status === 'connected';
  }

  /**
   * Start a connection attempt unless something already owns the next one. Every activation path
   * funnels through here — start, wake, retry — so the conditions are checked in one place.
   */
  private beginAttempt(): void {
    if (!this.active) return;
    if (this.ws) return; // open or still handshaking
    // A credential request is in flight for a generation that still matters.
    if (this.attemptGeneration && !this.attemptGeneration.signal.aborted) return;
    if (this.retryTimer !== null) return; // the backoff owns the next attempt
    if (this._state.status === 'disabled') return;
    void this.attempt();
  }

  /**
   * One attempt, credential included: stop if we shouldn't be connecting, mint a credential when
   * we have none or it is about to expire, then open the socket. Any failure along the way lands
   * on the same retry schedule.
   */
  private async attempt(): Promise<void> {
    if (!this.active || this.ws) return;
    if (document.hidden || !navigator.onLine) {
      this.deferToWake('Page hidden or offline');
      return;
    }

    if (this._state.status !== 'reconnecting') {
      this.state = { status: 'connecting' };
    }

    const generation = this.generation;
    const { signal } = generation;
    let credential = this.credential;

    if (!credential || this.isCredentialStale) {
      // The scheduler runs one attempt at a time, so this is all the single-flighting a credential
      // request needs — there is no second state machine that could ask in parallel.
      this.attemptGeneration = generation;

      let connection: RealtimeConnection | null;
      try {
        connection = await this.options.loadConnection(signal);
      } catch (err) {
        if (signal.aborted) return;
        console.error('[Realtime] Failed to fetch connection credential', err);
        this.state = { status: 'reconnecting' };
        this.scheduleRetry();
        return;
      } finally {
        if (this.attemptGeneration === generation) this.attemptGeneration = null;
      }

      // A stop or an identity change took over while we awaited; that flow owns what happens next.
      if (signal.aborted || !this.active) return;

      if (!connection) {
        this.enterDisabled();
        return;
      }

      credential = connection;
      this.credential = connection;
      this.credentialExpiresAt = Date.now() + connection.ttl * 1000;
    }

    // The browser may have gone to sleep or lost its network while the credential request was in
    // flight. Do not spend the fresh credential on a socket that is already doomed; a wake event
    // reuses it and continues this attempt.
    if (document.hidden || !navigator.onLine) {
      this.deferToWake('Page hidden or offline after loading credential');
      return;
    }

    this.openSocket(credential);
  }

  /**
   * Nothing can be connected from here. Remember we owe an attempt, and report the same status the
   * retry schedule reports while it waits — an attempt deferred to a wake event is not in progress,
   * and leaving it as `connecting` would show a live-looking indicator to an offline tab.
   */
  private deferToWake(reason: string): void {
    console.log(`[Realtime] ${reason}; will connect on wake`);
    this.reconnectOnWake = true;
    this.state = { status: 'reconnecting' };
  }

  /** No credential is minted for this identity: realtime is off. Distinct from a failed request. */
  private enterDisabled(): void {
    console.log('[Realtime] Realtime is disabled for this identity');
    this.invalidateCredential();
    this.retryCount = 0;
    this.state = { status: 'disabled' };
    // Nothing can subscribe, so no session may authorize; the interests stay recoverable.
    this.resetChannels();
  }

  /** Idempotent: duplicate (type, handler) registrations are discarded. */
  private armWakeListeners(): void {
    document.addEventListener('visibilitychange', this.handleWake);
    window.addEventListener('online', this.handleWake);
  }

  private disarmWakeListeners(): void {
    document.removeEventListener('visibilitychange', this.handleWake);
    window.removeEventListener('online', this.handleWake);
  }

  private clearRetryTimer(): void {
    if (this.retryTimer != null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private openSocket(credential: RealtimeConnection): void {
    let socket: WebSocket;
    try {
      socket = new WebSocket(credential.endpoint);
    } catch (error) {
      console.error('[Realtime] Failed to open WebSocket', error);
      this.state = { status: 'reconnecting' };
      this.scheduleRetry();
      return;
    }
    this.ws = socket;

    // The watchdog covers the socket handshake too. Until `onopen` fires nothing else is armed —
    // `beginAttempt` sees a socket and stands down — so a connect that is black-holed rather than
    // refused would otherwise wedge the attempt for as long as the browser is willing to wait.
    this.resetHeartbeat();

    socket.onopen = () => {
      console.log('[Realtime] WebSocket opened, sending connect command');

      this.rawSend({
        type: 'connect',
        ref: this.nextRef(),
        payload: { jwt: credential.token },
      });

      // The wire is up: the clock restarts for the `connected` frame.
      this.resetHeartbeat();
    };

    socket.onmessage = (event) => {
      this.resetHeartbeat();
      this.handleMessage(event.data);
    };

    socket.onclose = (event) => {
      // A superseding identity generation detached this socket before its close event arrived.
      if (this.ws !== socket) return;
      this.clearHeartbeat();
      console.log('[Realtime] WebSocket closed', { code: event.code });
      this.ws = null;
      this.state = { status: 'disconnected' };

      this.resetChannels();

      if (this.intentionalClose) return;

      if (event.code === 4001) {
        // The credential was refused. Discarding it makes the next attempt mint a fresh one, which
        // is an ordinary input to `attempt()` rather than a special case — but only once: a
        // credential minted seconds ago and refused again is not something retrying will fix.
        this.invalidateCredential();
        this.credentialRejections += 1;
        if (this.credentialRejections > 1) {
          console.error('[Realtime] Fresh connection credential rejected. Halting reconnects.');
          return;
        }
        console.warn('[Realtime] Connection credential rejected; requesting a fresh one');
        // The credential was the problem, not the network.
        this.retryCount = 0;
      }

      this.state = { status: 'reconnecting' };
      this.scheduleRetry();
    };
  }

  /** @internal Frames are only meaningful on a live connection; there is no outbound queue. */
  send(message: OutboundMessage): void {
    if (!this.isConnected || this.ws?.readyState !== WebSocket.OPEN) return;
    this.rawSend({ ...message, ref: message.ref || this.nextRef() });
  }

  private rawSend(message: OutboundMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private handleMessage(data: string) {
    let msg: InboundMessage;
    try {
      msg = JSON.parse(data);
    } catch {
      console.error('[Realtime] Failed to parse inbound message', data);
      return;
    }
    if (isInboundChannelMessage(msg)) {
      const ch = this.channels.get(msg.payload.channel);
      if (ch) {
        ch.handleMessage(msg);
      }
      return;
    }

    switch (msg.type) {
      case 'ping':
        this.rawSend({ type: 'pong', payload: { timestamp: msg.payload.timestamp } });
        break;

      case 'connected': {
        console.log(`[Realtime] connected user_id=${msg.payload.user_id}, connection_id=${msg.payload.connection_id}`);
        this.retryCount = 0;
        this.credentialRejections = 0;
        this.state = { status: 'connected', user: msg.payload };
        // Desired state is what recovers: every session with a live lease subscribes, the rest do
        // nothing. Nothing is replayed, so there is only one path back onto a channel.
        this.channels.forEach((ch) => ch.handleConnect());
        break;
      }
      case 'error': {
        const { code, message } = (msg as ConnectionError).payload;
        if (code === 'TOKEN_EXPIRED' || code === 'TOKEN_INVALID') {
          // Same recovery as a 4001, reached from a live socket: drop the credential and let the
          // close handler put us on the retry schedule, which mints a fresh one.
          console.warn(`[Realtime] Connection credential ${code}; reconnecting with a fresh one`);
          this.invalidateCredential();
          this.ws?.close();
        } else {
          console.error(`[Realtime] Connection error ${code}: ${message}`);
        }
        break;
      }
    }
  }

  private resetHeartbeat(): void {
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
    this.heartbeatTimer = setTimeout(() => {
      this.heartbeatTimer = null;
      console.warn(`[Realtime] No progress within ${this.heartbeatTimeout}ms; closing socket to trigger reconnect`);
      this.ws?.close(); // → onclose → scheduleRetry (intentionalClose stays false)
    }, this.heartbeatTimeout);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /** The session for `name`, created on demand. At most one live session exists per name. */
  channel(name: string): ChannelSession {
    let channel = this.channels.get(name);
    if (!channel) {
      channel = new ChannelSession(name, this);
      this.channels.set(name, channel);
    }
    return channel;
  }

  /**
   * @internal The session for `name` if one exists. Never creates one, so a render-phase read
   * cannot mutate the registry and a cleanup cannot resurrect a session it is tearing down.
   */
  findChannel(name: string): ChannelSession | undefined {
    return this.channels.get(name);
  }

  /**
   * Drop the sessions nobody holds, so the registry doesn't grow with every channel name visited.
   *
   * Nothing in flight can match: a pending token request, an unacknowledged subscribe and an
   * unacknowledged leave are all non-terminal channel states, and a release still inside its grace
   * window still wants to be subscribed. Deleting the entry is the whole disposal — release
   * already made the session inert and no consumer holds one across a commit, so collection does
   * the rest. This is insurance against a long session visiting many context objects, not a fix.
   */
  private sweepChannels(): void {
    for (const [name, channel] of this.channels) {
      if (channel.isIdle) this.channels.delete(name);
    }
  }

  /** The subscriptions didn't survive the socket; the sessions consumers still hold stand. */
  private resetChannels(): void {
    for (const channel of this.channels.values()) {
      channel.handleDisconnect();
    }
  }

  private get state() {
    return this._state;
  }

  private set state(state: ConnectionState) {
    this._state = state;
    if (state.status === 'connected') {
      setConnectionId(state.user.connection_id);
    } else {
      setConnectionId(null);
    }
    this._notifySubscribers();
  }

  private scheduleRetry(): void {
    if (!this.active) return;
    if (this.retryTimer !== null) return;
    if (document.hidden || !navigator.onLine) {
      this.deferToWake('Page hidden or offline');
      return;
    }
    this.retryCount++;
    const delay = RETRY_DELAYS[this.retryCount - 1] ?? RETRY_DELAY_MAX;

    console.log(`[Realtime] Reconnecting in ${delay} (attempt ${this.retryCount})`);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.beginAttempt();
    }, delay);
  }

  // External Store subscribers (used by React)
  private subscribers = new Set<() => void>();

  subscribe = (callback: () => void): (() => void) => {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  };

  getSnapshot = (): ConnectionState => {
    return this._state;
  };

  /** A value snapshot, so a consumer that only needs identity ignores lifecycle churn. */
  getConnectionIdSnapshot = (): string | null => {
    return this._state.status === 'connected' ? this._state.user.connection_id : null;
  };

  private _notifySubscribers() {
    this.subscribers.forEach((cb) => cb());
  }
}
